/**
 * WebAuthn utility functions for biometric login
 */

// Check if WebAuthn is supported by the browser
export const isWebAuthnSupported = (): boolean => {
  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    navigator.credentials.create &&
    navigator.credentials.get
  );
};

// Check if a platform authenticator (biometric) is available
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Convert base64url string to ArrayBuffer
export const base64urlToBuffer = (base64url: string): ArrayBuffer => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Convert ArrayBuffer to base64url string
export const bufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Prepare credential for sending to server
export const prepareCredentialForServer = (credential: PublicKeyCredential): object => {
  const response = credential.response as AuthenticatorAttestationResponse | AuthenticatorAssertionResponse;

  const result: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    },
  };

  // Registration response
  if ('attestationObject' in response) {
    (result.response as Record<string, string>).attestationObject = bufferToBase64url(
      response.attestationObject
    );
  }

  // Authentication response
  if ('authenticatorData' in response) {
    const authResponse = result.response as Record<string, string>;
    authResponse.authenticatorData = bufferToBase64url(response.authenticatorData);
    authResponse.signature = bufferToBase64url(response.signature);
    if (response.userHandle) {
      authResponse.userHandle = bufferToBase64url(response.userHandle);
    }
  }

  return result;
};

// Parse server registration options for browser API
export const parseRegistrationOptions = (
  options: Record<string, unknown>
): PublicKeyCredentialCreationOptions => {
  const user = options.user as Record<string, unknown>;
  const excludeCredentials = options.excludeCredentials as Array<Record<string, unknown>> | undefined;

  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge as string),
    user: {
      ...user,
      id: base64urlToBuffer(user.id as string),
    },
    excludeCredentials: excludeCredentials?.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id as string),
    })),
  } as PublicKeyCredentialCreationOptions;
};

// Parse server authentication options for browser API
export const parseAuthenticationOptions = (
  options: Record<string, unknown>
): PublicKeyCredentialRequestOptions => {
  const allowCredentials = options.allowCredentials as Array<Record<string, unknown>> | undefined;

  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge as string),
    allowCredentials: allowCredentials?.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id as string),
    })),
  } as PublicKeyCredentialRequestOptions;
};

// Get a user-friendly device name based on user agent
export const getDeviceName = (): string => {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android Device';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux PC';
  return 'Unknown Device';
};
